import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { authChangePassword } from "@/lib/api";

export default function ChangePassword() {
  const navigate = useNavigate();
  const { currentUser, signOut } = useApp();
  const [current, setCurrent] = useState("");
  const [pwd1, setPwd1] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd1.length < 6) return setError("New password must be at least 6 characters.");
    if (pwd1 !== pwd2) return setError("New password and confirmation do not match.");
    if (pwd1 === current) return setError("New password must be different from current.");
    setBusy(true);
    try {
      await authChangePassword(current, pwd1);
      toast.success("Password updated. Please sign in again.");
      signOut();
      navigate("/login", { replace: true });
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#faf8ff] p-6" data-testid="change-password-page">
      <div className="w-full max-w-md bg-white border border-[#e2e1eb] rounded-2xl shadow-sm p-7">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-5 h-5 text-yamaha-700" />
          <h1 className="text-[18px] font-bold text-[#1a1b22]">Change Password</h1>
        </div>
        <p className="text-[12.5px] text-[#747684] mb-5">
          Welcome <span className="font-semibold text-[#1a1b22]">{currentUser?.name}</span>. For
          security, please set a personal password before continuing.
        </p>
        {error && (
          <div data-testid="cp-error" className="mb-3 flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-700 text-[12px] rounded-lg p-2.5">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
        <form onSubmit={submit} className="space-y-3.5">
          <Field label="Current password" testid="cp-current" value={current} onChange={setCurrent} />
          <Field label="New password" testid="cp-new" value={pwd1} onChange={setPwd1} />
          <Field label="Confirm new password" testid="cp-confirm" value={pwd2} onChange={setPwd2} />
          <Button
            type="submit"
            data-testid="cp-submit"
            disabled={busy}
            className="w-full bg-yamaha-700 hover:bg-yamaha-800 text-white !h-10"
          >
            {busy ? "Updating..." : "Update Password"}
          </Button>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, testid }) => (
  <div>
    <label className="text-[11px] font-semibold uppercase text-[#747684]">{label}</label>
    <input
      data-testid={testid}
      type="password"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="input mt-1 !h-10 !text-[13.5px]"
    />
  </div>
);
